import React, { useCallback, useMemo } from 'react';
import { ColorResult, RGBColor } from 'react-color';

import { useTemplateOverrideMode } from '../../../../hooks/useTemplateOverrideMode';
import { IBaseUploadControlProps, IColorValueProps } from '../../../../types/templates';
import { areColorsEqual } from '../../../../utils/helpers';
import { ColorPicker } from '../ColorPicker/ColorPicker';

interface IProps extends IBaseUploadControlProps, IColorValueProps {
  onChange: (color: RGBColor) => void;
  onReset: () => void;
  onClearOverride?: () => void;
}

export const ColorPickerWithReset: React.FC<IProps> = ({
  mode,
  globalValue,
  overrideValue,
  defaultValue,
  onChange,
  onReset,
  onClearOverride,
  dataAction,
}) => {
  const { isPageOverrideMode, isOverridden, shouldClearOverride } = useTemplateOverrideMode({
    mode,
    overrideValue,
    onClearOverride,
  });

  const displayColor = useMemo(() => {
    if (isOverridden) {
      return overrideValue;
    }
    return globalValue || defaultValue;
  }, [isOverridden, overrideValue, globalValue, defaultValue]);

  const isCustomized = useMemo(() => {
    const currentColor = isOverridden ? overrideValue : globalValue;

    if (!currentColor) {
      return false;
    }

    return !areColorsEqual(currentColor, defaultValue);
  }, [isOverridden, overrideValue, globalValue, defaultValue]);

  const handleColorChange = useCallback(
    (color: ColorResult) => {
      onChange(color.rgb);
    },
    [onChange],
  );

  const handleReset = useCallback(() => {
    if (shouldClearOverride) {
      onClearOverride!();
    } else {
      onReset();
    }
  }, [shouldClearOverride, onClearOverride, onReset]);

  const showResetButton = isPageOverrideMode ? isOverridden : isCustomized;

  return (
    <ColorPicker
      rgb={displayColor}
      onChangeComplete={handleColorChange}
      onResetColor={showResetButton ? handleReset : undefined}
      dataAction={dataAction}
    />
  );
};
